import {yupResolver} from "@hookform/resolvers/yup";
import classNames from "classnames";
import {useEffect, useState} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {toast} from "react-hot-toast";

import {Alert} from "@/components/alert/Alert.tsx";
import Button from "@/components/button/Button.tsx";
import {Divider} from "@/components/divider/Divider.tsx";
import {Dot} from "@/components/dot/Dot.tsx";
import {ActionDialog} from "@/components/overlay/ActionDialog.tsx";
import {useZendeskWidgetOffset} from "@/lib/integrations/useZendeskWidgetOffset.ts";
import {QueryError, ResponseStatus, ServerErrors} from "@/lib/types/common.ts";
import {IDPInfo, SAMLSettings} from "@/lib/types/statuspages.ts";
import {setServerErrors} from "@/pages/vault/vaultForm/CredentialFormUtils.ts";

import {SAMLTesting} from "./SAMLTesting.tsx";
import {SSOSetupStep1V2} from "./SSOSetupStep1V2.tsx";
import {SSOFormSchema, SSOFormValues} from "./SSOSetupStep2.tsx";
import {SSOSetupStep2V2} from "./SSOSetupStep2V2.tsx";

import styles from "./SSOSetupV2.module.scss";

interface SSOSetupV2Props {
  metadataURL: string;
  samlSettings: SAMLSettings;
  testIDPs: IDPInfo[];
  updateSAML: (data: SSOFormValues) => Promise<{status: ResponseStatus} | QueryError | undefined>;
  removeSAML: () => Promise<boolean>;
}

export const SSOSetupV2 = ({metadataURL, samlSettings, testIDPs, updateSAML, removeSAML}: SSOSetupV2Props) => {
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<ServerErrors | undefined>({});
  useZendeskWidgetOffset("80px");
  const methods = useForm<SSOFormValues>({
    resolver: yupResolver<SSOFormValues>(SSOFormSchema),
    defaultValues: {
      idp_entity_id: samlSettings.idp_entity_id,
      idp_sso_target_url: samlSettings.idp_sso_target_url,
      idp_certificate: samlSettings.idp_certificate,
      use_sha1_for_signature: samlSettings.use_sha1_for_signature,
      unsigned_authn_requests: samlSettings.unsigned_authn_requests,
    },
  });

  const {handleSubmit, reset, formState} = methods;

  useEffect(() => {
    if (validationErrors) {
      setServerErrors(validationErrors, methods.setError, "");
    }
  }, [validationErrors, methods.setError]);

  const onSubmit = async (data: SSOFormValues) => {
    const result = await updateSAML(data);
    setValidationErrors({});
    if (result?.status === ResponseStatus.SUCCESS) {
      toast.success("SAML Settings have been updated");
    } else if (result?.status === ResponseStatus.ERROR) {
      const errorMessages = result as {message: {[key: string]: string[]}};
      if (errorMessages) {
        const errors: {[key: string]: string[]} = {};
        Object.entries(errorMessages.message).forEach(([field, messages]) => {
          errors[field] = messages;
        });
        setValidationErrors(errors as ServerErrors);
        toast.error("Some form fields need attention", {id: "SAML-form-errors"});
      }
    } else {
      toast.error("Oops.. something went wrong. Please contact our support if issue persists");
    }
  };

  const handleRemoveSAMLSettings = async () => {
    const result = await removeSAML();
    if (result) {
      toast.success("SAML Settings have been cleared");
      reset({
        idp_entity_id: "",
        idp_sso_target_url: "",
        idp_certificate: "",
        use_sha1_for_signature: false,
        unsigned_authn_requests: false,
      });
    } else {
      toast.error("Oops.. something went wrong. Please contact our support if issue persists");
    }
    setShowConfirm(false);
  };

  return (
    <div>
      <ActionDialog
        show={showConfirm}
        title="Disable SSO"
        onConfirm={handleRemoveSAMLSettings}
        onCancel={() => setShowConfirm(false)}
        cancelBtnText="Cancel"
        confirmBtnText="Disable"
        confirmBtnVariant="red"
      >
        Are you sure you want to remove your SAML SSO integration?
        <Alert className={"mt-4"} variant={"maintenance"} size={"large"} header={"Warning"}>
          Users will no longer be required to log in via SSO
        </Alert>
      </ActionDialog>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit, console.error)} className={"d-flex flex-column w-100 h-100"}>
          <div className={"flex-grow-1 overflow-y-auto px-4"}>
            <div className={"row"}>
              <div className={"col" + (testIDPs.length > 0 ? "-lg-8" : "")}>
                <SSOSetupStep1V2 metadataURL={metadataURL} samlSettings={samlSettings} />

                <Divider className="my-4" />

                <SSOSetupStep2V2 />
              </div>
              {testIDPs.length > 0 && (
                <div className="col-lg-3 offset-lg-1">
                  <hr className="d-lg-none mt-4" />
                  <SAMLTesting idps={testIDPs} wayflessURL={samlSettings.sp_wayfless_url} />
                </div>
              )}
            </div>
          </div>
          <div
            className={classNames(
              "position-sticky bottom-0 py-3 d-flex flex-column gap-3 pulse-bg-white",
              styles.stickyFooter
            )}
          >
            <Divider />
            <div className={"d-flex gap-2 px-3 ms-auto"}>
              {formState.isDirty && (
                <div className={"d-flex gap-2 align-items-center"}>
                  <Dot variant={"orange"} />
                  <span className={"pulse-color-orange-600 pulseTextBodySmall"}>Unsaved</span>
                </div>
              )}
              <Button variant={"secondary"} onClick={() => setShowConfirm(true)} className={"text-nowrap"}>
                Disable SAML SSO
              </Button>
              <Button type="submit" disabled={!formState.isDirty} className={"text-nowrap"}>
                Save Changes
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};
